import { useCallback, useEffect, useRef, useState } from 'react';
import finishSignIn from '../utils/finishSignIn';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getLocalStorage } from '../utils/func';
import config from '../utils/config';
import { abortable } from '../utils/abortable';

const useFetch = (service) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const mounted = useRef(false);
  const generation = useRef(0);
  const controller = useRef(null);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      generation.current += 1;
      controller.current?.abort();
    };
  }, []);

  const handleLogin = useCallback(async (response) => {
    if (!mounted.current) return undefined;
    const request = ++generation.current;
    controller.current?.abort();
    const abort = new AbortController();
    controller.current = abort;
    const current = () => mounted.current && generation.current === request;
    const startingToken = getLocalStorage(config.token);
    const ownsSession = () => current() && !abort.signal.aborted && getLocalStorage(config.token) === startingToken;
    setLoading(true);
    setError('');
    const timeout = setTimeout(() => abort.abort(), 25000);
    try {
      const result = await abortable(fetch(process.env.REACT_APP_API_URL + '/user/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(response.code !== undefined
          ? { service, code: response.code, code_verifier: response.code_verifier, nonce: response.nonce }
          : { credential: response.credential, service }),
        signal: abort.signal,
      }), abort.signal);
      if (!ownsSession()) return undefined;
      const data = await abortable(result.json(), abort.signal);
      if (!ownsSession()) return undefined;
      if (result.ok === false) throw new Error(data?.message || 'Sign-in failed');
      return await abortable(finishSignIn(data, { i18n, navigate, isCurrent: ownsSession,
        registration: response.registration, signal: abort.signal }), abort.signal);
    } catch (failure) {
      if (current()) setError(failure?.message || 'Sign-in failed');
      return undefined;
    } finally {
      clearTimeout(timeout);
      if (current()) setLoading(false);
    }
  }, [service, navigate, i18n]);

  return { loading, error, handleLogin };
};

export default useFetch;
